import { Vector3 } from "three";
import { cylinderDistance, type CylinderSurface } from "./cylinder-surface";
import {
  isCylinderFitCaptureActive,
  recordCylinderFitCapture,
} from "./cylinder-fit-replay";
import { fitCylinderInline, type CylinderFitter } from "./cylinder-fitting";

export type CylinderFitDiagnostic = {
  outcome: "accepted" | "rejected";
  reason:
    | "cancelled"
    | "insufficient-samples"
    | "insufficient-span"
    | "already-fitted"
    | "no-improvement"
    | "residual"
    | "insufficient-inliers"
    | "unstable-fit"
    | "accepted";
  sampleCount: number;
  axialSpan: number;
  elapsedMs: number;
  before: { center: number[]; axis: number[]; radius: number };
  after?: { center: number[]; axis: number[]; radius: number };
  rmsBefore?: number;
  rmsAfter?: number;
};

const MAX_SAMPLES = 2048;
const MAX_BUCKETS = 64;
const SAMPLES_PER_BUCKET = MAX_SAMPLES / MAX_BUCKETS;
const MIN_SAMPLES = 50;
const LIMITS = {
  axisDivisor: 9,
  corridorRadius: 0.75,
  sampleMultiplier: 1664525,
  sampleIncrement: 1013904223,
  minimumSpan: 0.25,
  minimumBucketWidth: 0.05,
  inlierRatio: 0.8,
  minimumErrorRatio: 0.0025,
  improvementRatio: 0.99,
};
const AXIS_SEARCH_SLOPE = Math.tan(Math.PI / LIMITS.axisDivisor);

/**
 * @param cylinder Current approximate cylinder.
 * @param tolerance Final selection tolerance, not the search distance.
 * @param axialDistance Distance from the initial seed along the axis.
 * @returns Radial evidence-search allowance including angular uncertainty.
 */
export function cylinderSearchMargin(
  cylinder: CylinderSurface,
  tolerance: number,
  axialDistance: number,
): number {
  return (
    Math.max(cylinder.radius * LIMITS.corridorRadius, tolerance * 4) +
    Math.abs(axialDistance) * AXIS_SEARCH_SLOPE
  );
}

/** Bounded observed-point evidence for refining one straight cylinder during growth. */
export class CylinderRefinement {
  private buckets = new Map<number, { points: Vector3[]; seen: number }>();
  private bucketWidth: number;
  private seen = 0;
  private fittedCount = 0;
  private spacing = Infinity;

  /**
   * @param initial Original cylinder, defining the evidence sampling frame only.
   * @param anchor Click-local evidence origin, not a point constrained to the fitted surface.
   * @param tolerance Existing selection tolerance, never widened by refinement.
   * @param onDiagnostic Optional local fit-observation hook; does not capture point data.
   * @param fitCylinder Joint geometric fitter; production supplies an owned worker.
   */
  constructor(
    private readonly initial: CylinderSurface,
    private readonly anchor: Vector3,
    private readonly tolerance: number,
    private readonly onDiagnostic?: (diagnostic: CylinderFitDiagnostic) => void,
    private readonly fitCylinder: CylinderFitter = fitCylinderInline,
  ) {
    this.bucketWidth = Math.max(LIMITS.minimumBucketWidth, initial.radius);
  }

  /**
   * @param point Loaded, visible, unclipped scan point; copied when retained.
   * @param cylinder Current accepted model.
   * @param spacing Nominal source spacing in world metres, when available.
   */
  add(point: Vector3, cylinder: CylinderSurface, spacing = Infinity): void {
    const axial =
      (point.x - this.anchor.x) * this.initial.axis.x +
      (point.y - this.anchor.y) * this.initial.axis.y +
      (point.z - this.anchor.z) * this.initial.axis.z;
    if (
      !point.toArray().every(Number.isFinite) ||
      Math.abs(cylinderDistance(cylinder, point)) >
        cylinderSearchMargin(cylinder, this.tolerance, axial)
    ) {
      return;
    }
    this.seen++;
    if (Number.isFinite(spacing) && spacing > 0) {
      this.spacing = Math.min(this.spacing, spacing);
    }
    const key = Math.floor(axial / this.bucketWidth);
    let bucket = this.buckets.get(key);
    if (!bucket) {
      bucket = { points: [], seen: 0 };
      this.buckets.set(key, bucket);
    }
    bucket.seen++;
    if (bucket.points.length < SAMPLES_PER_BUCKET) {
      bucket.points.push(point.clone());
    } else {
      const index =
        ((Math.imul(bucket.seen, LIMITS.sampleMultiplier) +
          LIMITS.sampleIncrement) >>>
          0) %
        bucket.seen;
      if (index < SAMPLES_PER_BUCKET) bucket.points[index].copy(point);
    }
    while (this.buckets.size > MAX_BUCKETS) {
      const merged = new Map<number, { points: Vector3[]; seen: number }>();
      for (const [index, previous] of this.buckets) {
        const nextKey = Math.floor(index / 2);
        const existing = merged.get(nextKey);
        if (existing) {
          const combined = [...existing.points, ...previous.points];
          const stride = Math.ceil(combined.length / SAMPLES_PER_BUCKET);
          existing.points = combined.filter(
            (_, position) => position % stride === 0,
          );
          existing.seen += previous.seen;
        } else {
          merged.set(nextKey, previous);
        }
      }
      this.buckets = merged;
      this.bucketWidth *= 2;
    }
  }

  /**
   * @param cylinder Accepted fitted model.
   * @returns Copied observed inliers near the click's axial neighborhood, not old model vertices.
   */
  seedPoints(cylinder: CylinderSurface): Vector3[] {
    const offset = new Vector3();
    const axialReach = Math.max(
      this.initial.radius,
      cylinder.radius,
      LIMITS.minimumBucketWidth * 2,
    );
    const candidates = [...this.buckets.values()]
      .flatMap((bucket) => bucket.points)
      .filter(
        (point) =>
          Math.abs(
            offset.subVectors(point, this.anchor).dot(this.initial.axis),
          ) <= axialReach &&
          Math.abs(cylinderDistance(cylinder, point)) <= this.tolerance,
      );
    candidates.sort(
      (left, right) =>
        left.distanceToSquared(this.anchor) -
        right.distanceToSquared(this.anchor),
    );
    if (!candidates.length) return [];
    const [nearest] = candidates;
    const reach = Math.max(cylinder.radius / 2, this.tolerance * 3);
    return candidates
      .filter((point) => point.distanceToSquared(nearest) <= reach * reach)
      .map((point) => point.clone());
  }

  /**
   * @param current Last accepted model.
   * @param cancelled Cancellation/source-generation check.
   * @returns A materially better straight fit, or undefined without mutation.
   */
  async refine(
    current: CylinderSurface,
    cancelled: () => boolean,
  ): Promise<CylinderSurface | undefined> {
    const points = [...this.buckets.values()].flatMap(
      (bucket) => bucket.points,
    );
    const started = performance.now();
    let axialSpan = 0;
    const report = (
      reason: CylinderFitDiagnostic["reason"],
      fitted?: CylinderSurface,
      before?: number,
      after?: number,
    ): void => {
      if (!this.onDiagnostic && !isCylinderFitCaptureActive()) return;
      function model(
        cylinder: CylinderSurface,
      ): CylinderFitDiagnostic["before"] {
        return {
          center: cylinder.center.toArray(),
          axis: cylinder.axis.toArray(),
          radius: cylinder.radius,
        };
      }
      const diagnostic: CylinderFitDiagnostic = {
        outcome: reason === "accepted" ? "accepted" : "rejected",
        reason,
        sampleCount: points.length,
        axialSpan,
        elapsedMs: performance.now() - started,
        before: model(current),
        after: fitted ? model(fitted) : undefined,
        rmsBefore: before === undefined ? undefined : Math.sqrt(before),
        rmsAfter: after === undefined ? undefined : Math.sqrt(after),
      };
      recordCylinderFitCapture(points, this.anchor, current, diagnostic);
      this.onDiagnostic?.(diagnostic);
    };
    if (
      cancelled() ||
      points.length < MIN_SAMPLES ||
      this.seen - this.fittedCount < MIN_SAMPLES
    ) {
      report(cancelled() ? "cancelled" : "insufficient-samples");
      return;
    }
    this.fittedCount = this.seen;
    const offset = new Vector3();
    let minimum = Infinity;
    let maximum = -Infinity;
    for (const point of points) {
      const axial = offset.subVectors(point, current.center).dot(current.axis);
      minimum = Math.min(minimum, axial);
      maximum = Math.max(maximum, axial);
    }
    axialSpan = maximum - minimum;
    if (axialSpan < LIMITS.minimumSpan) {
      report("insufficient-span");
      return;
    }
    function score(cylinder: CylinderSurface): number {
      const residuals = points
        .map((point) => Math.abs(cylinderDistance(cylinder, point)))
        .sort((left, right) => left - right);
      const count = Math.floor(residuals.length * LIMITS.inlierRatio);
      let sum = 0;
      for (let index = 0; index < count; index++) sum += residuals[index] ** 2;
      return sum / count;
    }
    const originalError = score(current);
    if (originalError < this.tolerance ** 2 * LIMITS.minimumErrorRatio) {
      report("already-fitted", current, originalError, originalError);
      return;
    }
    const fitted = await this.fitCylinder(
      points,
      current,
      Math.min(this.spacing, this.tolerance / 3),
      cancelled,
    );
    if (cancelled() || !fitted.accepted) {
      report(
        cancelled() ? "cancelled" : "unstable-fit",
        fitted.cylinder,
        originalError,
      );
      return;
    }
    const best = fitted.cylinder;
    const error = score(best);
    if (
      cancelled() ||
      best === current ||
      maximum - minimum < best.radius * 4 ||
      error > originalError * LIMITS.improvementRatio ||
      error > this.tolerance ** 2
    ) {
      let reason: CylinderFitDiagnostic["reason"] = "no-improvement";
      if (error > this.tolerance ** 2) reason = "residual";
      if (maximum - minimum < best.radius * 4) reason = "insufficient-span";
      if (cancelled()) reason = "cancelled";
      report(reason, best, originalError, error);
      return;
    }
    if (
      points.filter(
        (point) => Math.abs(cylinderDistance(best, point)) <= this.tolerance,
      ).length <
      points.length * LIMITS.inlierRatio
    ) {
      report("insufficient-inliers", best, originalError, error);
      return;
    }
    best.center.addScaledVector(
      best.axis,
      current.center.clone().sub(best.center).dot(best.axis),
    );
    best.radial.copy(current.radial).projectOnPlane(best.axis).normalize();
    report("accepted", best, originalError, error);
    return best;
  }
}
